import React from "react";
import { Link } from "react-router-dom";
import { Github, Linkedin, Mail } from "lucide-react";
import GitPrompt from "@/components/GitPrompt";
import { siteConfig } from "@/data/site";
import { useTimeTheme } from "@/hooks/use-time-theme";

type UseItem = {
  name: string;
  description?: string;
  link?: string;
};

type UseSection = {
  emoji: string;
  title: string;
  items: UseItem[];
};

const usesSections: UseSection[] = [
  {
    emoji: "💻",
    title: "Editor & Terminal",
    items: [
      {
        name: "VS Code",
        description: "Primary code editor with essential extensions",
      },
      {
        name: "Cursor",
        description: "AI-powered editor for faster development",
      },
    ],
  },
  {
    emoji: "🖥️",
    title: "Hardware",
    items: [
      {
        name: "MacBook Pro M5",
        description: "Primary development machine",
      },
      {
        name: "External Monitor",
        description: "27-inch 4K display for extended workspace",
      },
      {
        name: "Mechanical Keyboard",
        description: "For comfortable long coding sessions",
      },
    ],
  },
  {
    emoji: "⚡",
    title: "Productivity",
    items: [
      {
        name: "Raycast",
        description: "Fast launcher and productivity tool",
      },
      {
        name: "Obsidian",
        description: "Knowledge base and note-taking with markdown",
      },
      {
        name: "Notion",
        description: "Planning and documentation",
      },
    ],
  }
];

const Uses = () => {
  const theme = useTimeTheme();

  return (
    <div className={`min-h-screen ${theme.background} ${theme.text} transition-colors duration-1000`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header - Matching home page style */}
        <header className="pt-8 sm:pt-12 md:pt-16 pb-12 sm:pb-16 md:pb-20">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <Link
              to="/"
              className={`text-xl sm:text-2xl md:text-3xl font-bold ${theme.accent} hover:opacity-80 transition-all text-center sm:text-left`}
            >
              {siteConfig.name.split(" ")[0].toLowerCase()}.space
            </Link>
            
            {/* Navigation - Centered on mobile, right-aligned on large screens */}
            <nav className={`flex flex-wrap items-center justify-center sm:justify-end gap-4 sm:gap-6 text-sm sm:text-base ${theme.accent}/80`}>
              <Link to="/about" className="hover:opacity-100 transition-colors">
                About
              </Link>
              <Link to="/blog" className="hover:opacity-100 transition-colors">
                Blog
              </Link>
              <Link to="/projects" className="hover:opacity-100 transition-colors">
                Projects
              </Link>
              <Link to="/uses" className="hover:opacity-100 transition-colors">
                Uses
              </Link>
              <Link to="/opensource" className="hover:opacity-100 transition-colors">
                Open Source
              </Link>
            </nav>
          </div>
        </header>

        {/* Main Content */}
        <main className="pb-12 sm:pb-16">
          <section className="flex flex-col items-center">
            <div className="max-w-2xl w-full space-y-8">
              {/* Git Prompt */}
              <GitPrompt command="cat .toolrc" className="justify-center sm:justify-start" />
              
              {/* Header */}
              <div className="space-y-4">
                <h1 className={`text-2xl sm:text-3xl md:text-4xl font-bold leading-tight ${theme.text}`}>
                  Uses
                </h1>
                <p className={`text-base sm:text-lg ${theme.text}/70`}>
                  A living document of the tools, apps, and hardware I use daily to build software and stay productive.
                </p>
              </div>

              {/* Uses Sections */}
              <div className="space-y-10 mt-10">
                {usesSections.map((section) => (
                  <div key={section.title} className="space-y-4">
                    <h2 className={`text-lg sm:text-xl font-semibold ${theme.text} flex items-center gap-2`}>
                      <span>{section.emoji}</span>
                      <span>{section.title}</span>
                    </h2>
                    <div className="space-y-3">
                      {section.items.map((item, index) => (
                        <div key={index} className="space-y-1">
                          <h3 className={`font-medium ${theme.text}`}>
                            {item.link ? (
                              <a
                                href={item.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`${theme.accent} hover:opacity-80 underline transition-colors`}
                              >
                                {item.name}
                              </a>
                            ) : (
                              item.name
                            )}
                          </h3>
                          {item.description && (
                            <p className={`text-sm ${theme.text}/60`}>
                              {item.description}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer note */}
              <p className={`text-sm mt-12 ${theme.text}/50 italic`}>
                This page is updated periodically as my setup evolves.
              </p>
            </div>
          </section>

          {/* Social Links and Footer - Matching home page style */}
          <section className={`mt-16 sm:mt-20 md:mt-24 flex flex-col items-center gap-6 ${theme.text}/60`}>
            <div className="flex items-center gap-6">
              <a
                href={siteConfig.social.github}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
                className="transition-colors hover:opacity-100"
              >
                <Github className="h-6 w-6 sm:h-7 sm:w-7" />
              </a>
              <a
                href={siteConfig.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="transition-colors hover:opacity-100"
              >
                <Linkedin className="h-6 w-6 sm:h-7 sm:w-7" />
              </a>
              <a
                href={`mailto:${siteConfig.email}`}
                aria-label="Email"
                className="transition-colors hover:opacity-100"
              >
                <Mail className="h-6 w-6 sm:h-7 sm:w-7" />
              </a>
            </div>
            <p className="text-xs sm:text-sm flex flex-wrap items-center justify-center gap-2">
              <span>&gt; built with</span>
              <span className="inline-flex h-4 overflow-hidden align-middle">
                <span className="rotate-words">
                  <span>Cursor</span>
                  <span>love</span>
                  <span>coffee</span>
                  <span>code</span>
                </span>
              </span>
            </p>
          </section>
        </main>
      </div>
    </div>
  );
};

export default Uses;
